import {
  FeedbackCampaign,
  FeedbackSummaryInsights,
  FormResponses,
  GroupDisplay,
  GroupDto,
  UserSummary,
} from "@coaching-culture/types";
import {
  Box,
  Button,
  Circle,
  Flex,
  IconButton,
  Input,
  Loader,
  Modal,
  MultiSelect,
  Panel,
  SortableTable,
  SortableTableColumn,
  Text,
  typeSize,
} from "@coaching-culture/ui";
import { useUser } from "auth";
import axios from "axios";
import CenterColumn from "components/CenterColumn";
import {
  buildFeedbackCsv,
  FeedbackInsightsDisplay,
} from "components/FeedbackInsights";
import { PageHeader } from "components/PageHeader";
import { RatingStars } from "components/RatingInsights";
import { UserFlag } from "components/UserFlag";
import { getUnixTime } from "date-fns";
import { uniqBy } from "lodash";
import { useDeleteUserFromCampaign } from "queries/feedback";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FaTrash, FaUserPlus, FaUsers } from "react-icons/fa";
import { useParams } from "react-router";
import styled from "styled-components";
import { downloadContent } from "utils";
import { GroupSelectModal } from "./GroupSelectModal";
import { UserSelectModal } from "./UserSelectModal";

const StarsCell = styled(Flex)`
  font-size: ${typeSize("small")};
`;

const searchUsers = (item: FeedbackSummaryInsights["users"][number]) =>
  [item.name, item.email].join(" ");

function DeleteCampaignUserModal({
  email,
  onCancel,
  onConfirm,
}: {
  email: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const [confirmEmail, setConfirmEmail] = useState("");

  return (
    <Modal width={400}>
      <Box p={4}>
        <Text mb={2} fontWeight={600} fontSize={4}>
          Remove User
        </Text>
        <Text fontSize={3}>
          Are you sure you want to remove the user from this campaign? This will
          completely remove all results gathered for this user and is
          irreversible. If you are sure then please enter the users email
          address into the box to confirm.
        </Text>

        <Text fontWeight={600} mt={2}>
          Email Address
        </Text>
        <Input
          value={confirmEmail}
          onChange={(val) => setConfirmEmail(val.target.value)}
        />
        <Flex mt={3} alignItems={"center"} flexDirection={"column"}>
          <Flex>
            <Button
              disabled={confirmEmail !== email}
              mr={2}
              onClick={onConfirm}
              color={"danger"}
            >
              Delete
            </Button>
            <Button onClick={() => onCancel()} color="primary">
              Cancel
            </Button>
          </Flex>
        </Flex>
      </Box>
    </Modal>
  );
}

export function Campaign() {
  const { id } = useParams<{ id: string }>();
  const [item, setItem] = useState<FeedbackCampaign | null>(null);
  const [report, setReport] = useState<FeedbackSummaryInsights | null>(null);
  const [groupFilter, setGroupFilter] = useState<string[]>([]);
  const [showAddUserModal, setShowAddUserModal] = useState<boolean>(false);
  const [showAddGroupModal, setShowAddGroupModal] = useState(false);
  const [deletingUser, setDeleteingUser] = useState<{
    email: string;
    id: string;
  }>(null);
  const [user] = useUser();
  const deleteUserFromCampaign = useDeleteUserFromCampaign(id);

  const canView = useMemo(() => {
    if (item == null) {
      return false;
    }

    if (item.resultsVisibility.includes("admin")) {
      return true;
    }

    return item.viewers.map((x) => x.id).includes(user.id);
  }, [item, user]);

  const groupOptions = useMemo(() => {
    if (report == null) {
      return [];
    }

    return uniqBy(
      report.users.flatMap((x) => x.groups),
      (x) => x.id,
    ).map((x) => ({
      value: x.id.toString(),
      label: x.name,
    }));
  }, [report]);

  const nudgeUser = useCallback(
    (feedbackUserId: string) => {
      axios
        .put(`/api/v2/feedback-campaigns/${id}/users/${feedbackUserId}/nudge`)
        .then(() => {
          const u = report.users.find(
            (x) => x.feedbackCampaignUserId === feedbackUserId,
          );
          u.lastNudged = getUnixTime(new Date());
          setReport({ ...report });
        });
    },
    [id, report],
  );

  const removeGroup = useCallback(
    (groupId: string) => {
      if (
        window.confirm(
          "Are you sure you want to remove this group? It will not remove any users added from the group but will stop new users from being added",
        )
      ) {
        axios
          .delete(`/api/v2/feedback-campaigns/${id}/groups/${groupId}`)
          .then(() => {
            setItem({
              ...item,
              groups: item.groups.filter((x) => x.id !== groupId),
            });
          });
      }
    },
    [id, item],
  );

  const users = useMemo(() => {
    if (report == null) {
      return [];
    }

    if (groupFilter.length === 0) {
      return report.users;
    }

    return report.users.filter((x) =>
      x.groups.map((x) => x.id).some((x) => groupFilter.includes(x)),
    );
  }, [groupFilter, report]);

  const pull = useCallback((id: string) => {
    axios.get(`/api/feedback/campaigns/${id}`).then(({ data }) => {
      setItem(data);
    });
    axios.get(`/api/feedback/campaigns/${id}/report`).then(({ data }) => {
      setReport(data);
    });
  }, []);

  const items = useMemo(() => {
    if (report == null) {
      return [];
    }

    const compIds = users.flatMap((x) => x.completionIds);

    const items = report.items.map<FormResponses>((x) => {
      if ("responses" in x) {
        return {
          ...x,
          responses: (x.responses as any).filter((x) =>
            compIds.includes(x.completionId),
          ),
        };
      } else {
        return x;
      }
    });

    return items;
  }, [users, report]);

  const exportCallback = useCallback(
    (userId: string) => {
      const userReport = report.users.find((x) => x.id === userId);

      downloadContent(
        buildFeedbackCsv(
          items as FormResponses[],
          item,
          userReport.completionIds,
        ),
        `feedback.csv`,
      );
    },
    [report, items, item],
  );

  const groupCols = useMemo<SortableTableColumn<GroupDisplay>[]>(
    () => [
      {
        name: "name",
        label: "Name",
        format: (x) => (
          <Flex alignItems={"center"}>
            <Circle icon={FaUsers} color="primary" mr={1} />
            <Text>{x.name}</Text>
          </Flex>
        ),
      },
      {
        name: "memberCount",
        label: "Member count",
      },
      {
        name: "",
        label: "",
        format: (x) => (
          <Flex alignItems={"center"} justifyContent="flex-end">
            <IconButton
              icon={FaTrash}
              color="danger"
              onClick={() => removeGroup(x.id)}
            />
          </Flex>
        ),
      },
    ],
    [removeGroup],
  );

  const cols = useMemo<
    SortableTableColumn<FeedbackSummaryInsights["users"][number]>[]
  >(() => {
    const getCampaignId = (userId: string) =>
      item.users.find((x) => x.id === userId)?.campaignUserId;

    return [
      {
        name: "name",
        label: "User",
        format: (x) => (
          <UserFlag
            user={x}
            to={
              canView
                ? `/solutions/feedback/campaigns/${getCampaignId(x.id)}`
                : undefined
            }
          />
        ),
        sortFormat: (x) => x.name.toLowerCase(),
      },
      {
        name: "respondentCount",
        label: "Respondents",
        headingProps: {
          textAlign: "right",
        },
        props: {
          textAlign: "right",
        },
        format: (x) => (
          <Flex alignItems={"center"} justifyContent={"flex-end"}>
            {x.respondentCount === 0 && (
              <Button
                tiny
                onClick={() => nudgeUser(x.feedbackCampaignUserId)}
                mr={2}
                disabled={x.lastNudged != null}
              >
                {x.lastNudged == null ? "Nudge?" : "Nudged"}
              </Button>
            )}
            {x.respondentCount}
          </Flex>
        ),
      },
      {
        name: "responseCount",
        label: "Responses",
        headingProps: {
          textAlign: "right",
        },
        props: {
          textAlign: "right",
        },
      },
      {
        name: "averageScore",
        label: "Average score",
        headingProps: {
          textAlign: "right",
        },
        props: {
          textAlign: "right",
        },
        sortFormat: (x) => x.averageScore?.value ?? -1,
        format: (x) =>
          x.averageScore == null ? (
            "N/A"
          ) : x.averageScore.type === "stars" ? (
            <StarsCell justifyContent="flex-end">
              <RatingStars value={x.averageScore.value * 5} />
            </StarsCell>
          ) : (
            <Text>{(x.averageScore.value * 10).toFixed(1)}</Text>
          ),
      },
      {
        name: "actions",
        label: "",
        headingProps: {
          textAlign: "right",
          width: "200",
        },
        format: (x) =>
          !canView ? (
            ""
          ) : (
            <Flex justifyContent={"center"}>
              {!item.archived && item.users.length > 1 && (
                <IconButton
                  title="Delete"
                  mr={2}
                  icon={FaTrash}
                  color={"danger"}
                  onClick={() => setDeleteingUser({ email: x.email, id: x.id })}
                />
              )}
              <Button tiny onClick={() => exportCallback(x.id)}>
                Export
              </Button>
            </Flex>
          ),
      },
    ];
  }, [item, canView, nudgeUser, exportCallback]);

  useEffect(() => {
    pull(id);
  }, [id, pull]);

  if (item == null) {
    return <Loader />;
  }

  const addUsers = (users: UserSummary[]) => {
    setShowAddUserModal(false);
    if (users.length > 0) {
      axios
        .post(`/api/v2/feedback-campaigns/${id}/users`, {
          userIds: users.map((x) => x.id),
          groupIds: [],
        })
        .then(() => {
          pull(id);
        });
    }
  };

  const addGroups = (items: GroupDto[]) => {
    setShowAddGroupModal(false);
    if (items.length > 0) {
      axios
        .post(`/api/v2/feedback-campaigns/${id}/users`, {
          userIds: [],
          groupIds: items.map((x) => x.id),
        })
        .then(() => {
          pull(id);
        });
    }
  };

  return (
    <>
      {showAddUserModal && <UserSelectModal onSelect={addUsers} />}
      {showAddGroupModal && <GroupSelectModal onSelect={addGroups} />}
      {deletingUser && (
        <DeleteCampaignUserModal
          email={deletingUser.email}
          onCancel={() => setDeleteingUser(null)}
          onConfirm={() => {
            deleteUserFromCampaign
              .mutateAsync(deletingUser.id)
              .then(() => pull(id));
            setDeleteingUser(null);
          }}
        />
      )}
      <CenterColumn>
        <PageHeader
          text={item.name}
          subtitle="An overview of this campaign"
          backUrl="/success/feedback"
        />
        <Flex justifyContent="space-between" mb={1}>
          <Flex alignItems="center">
            <Text fontSize={4} fontWeight={500}>
              Users
            </Text>
            <Button
              ml={2}
              icon={FaUserPlus}
              color="primary"
              onClick={() => setShowAddUserModal(true)}
            >
              Add Users
            </Button>
          </Flex>
          <Flex alignItems="center">
            <Text mr={2}>Filter by group</Text>
            <MultiSelect
              disabled={groupOptions.length === 0}
              value={groupFilter}
              onChange={setGroupFilter}
              options={groupOptions}
              defaultLabel="All Groups"
            />
          </Flex>
        </Flex>
        {!item.sendEmail && (
          <Text>Please note user emails are disabled for this campaign</Text>
        )}
        {item.isDraft && (
          <Text fontWeight={600} color={"warning"}>
            This campaign is currently a draft and has not been sent to users
          </Text>
        )}

        <Panel mb={3}>
          {report == null ? (
            <Loader />
          ) : (
            <SortableTable
              columns={cols}
              data={users}
              paged
              rowSearch={searchUsers}
            />
          )}
        </Panel>
        <Flex justifyContent="space-between" mb={1}>
          <Flex alignItems="center">
            <Text fontSize={4} fontWeight={500}>
              Groups
            </Text>
            <Button
              ml={2}
              icon={FaUsers}
              color="primary"
              onClick={() => setShowAddGroupModal(true)}
            >
              Add Groups
            </Button>
          </Flex>
        </Flex>
        <Panel mb={3}>
          {report == null ? (
            <Loader />
          ) : (
            <SortableTable
              columns={groupCols}
              data={item.groups}
              emptyMessage="No groups added yet."
            />
          )}
        </Panel>
        <FeedbackInsightsDisplay
          title="Campaign users overall report"
          insights={items}
          relationships={[]}
          highlightedUser={null}
          showResponses={false}
          campaign={item}
        />
      </CenterColumn>
    </>
  );
}
